<template>
  <section :class="$style.section" :aria-label="t('aria')">
    <header :class="$style.head">
      <h2 :class="$style.title">
        <ui-icon name="gift" :class="$style.titleIcon" />
        {{ t('title') }}
      </h2>
      <span :class="$style.xp">
        <ui-icon name="xp" :class="$style.xpIcon" />
        <span :class="$style.xpCurrent">{{ formattedXp }}</span>
        <span v-if="targetXp" :class="$style.xpTotal">
          / {{ formattedTarget }}
        </span>
      </span>
    </header>

    <div v-if="levels.length" :class="$style.body">
      <!-- Каждая награда — колонка: сегмент шкалы (XP от предыдущего порога + 1 до
           points_required этого уровня, для первого — от 0) и карточка ниже. -->
      <ul :class="$style.rewardsRow">
        <li
          v-for="(level, index) in levels"
          :key="level.id"
          :class="[
            $style.rewardCol,
            $style[`rewardCol-${level.status}`],
            {
              [$style.rewardColDouble]:
                getDisplayedRewardItems(level).length > 1
            }
          ]"
        >
          <div :class="$style.segmentTrack" aria-hidden="true">
            <div :class="$style.segmentTrackBg" />
            <div
              :class="$style.segmentFill"
              :style="{ width: segmentFillPercent(index) }"
            />
          </div>

          <button
            type="button"
            :class="$style.cellInner"
            :disabled="!isCtaActive(level)"
            @click="onClick(level)"
          >
            <span
              :class="[
                $style.cellTop,
                level.status === 'locked' ? $style.cellTopLocked : undefined
              ]"
            >
              <ui-icon
                v-if="level.status === 'locked'"
                name="xp"
                :class="$style.cellXpIcon"
              />
              <span :class="$style.cellStatus">
                <span
                  v-if="level.claim_blocked_reason === 'auto_claim_imminent'"
                  :class="$style.cellHint"
                >
                  {{ t('blocked') }}
                </span>
                <span v-else>
                  {{ statusLabel(level) }}
                </span>
              </span>
            </span>

            <span
              :class="[
                $style.cellMedia,
                getDisplayedRewardItems(level).length > 1
                  ? $style.cellMediaDouble
                  : undefined
              ]"
            >
              <template
                v-for="(item, i) in getDisplayedRewardItems(level)"
                :key="item.id"
              >
                <span
                  v-if="i > 0"
                  :class="$style.cellMediaPlus"
                  aria-hidden="true"
                >
                  <ui-icon name="plus" aria-hidden="true" />
                </span>
                <span :class="$style.cellMediaItem">
                  <span :class="$style.cellImage">
                    <ui-icon
                      v-if="level.status === 'claimed'"
                      name="checks"
                      :class="$style.cellImageCheck"
                      aria-hidden="true"
                    />
                    <img
                      v-if="rewardImage(item)"
                      :src="rewardImage(item)!"
                      :alt="item.resource.name"
                      loading="lazy"
                    />
                    <span v-else :class="$style.cellImageFallback">{{
                      i + 1
                    }}</span>
                  </span>

                  <span :class="$style.cellName">
                    {{ item.resource.name }}
                    <template v-if="item.amount > 1">
                      {{ item.amount }}
                    </template>
                  </span>
                </span>
              </template>
            </span>
          </button>
        </li>
      </ul>
    </div>

    <p v-else-if="!isLoading" :class="$style.empty">{{ t('empty') }}</p>
  </section>
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAccountStore } from '~/stores/account'
import UiIcon from '~/ui-core/components/icon/index.vue'
import { useRewardsStore } from '../../stores/rewards'
import { usePickemAnalytics } from '../../composables/use-pickem-analytics'
import { usePickemCounter } from '../../composables/use-pickem-counter'
import { getDisplayedRewardItems } from '../../utils/display-reward-items'
import type { Tournament, RewardLevel, RewardItem } from '../../types'

defineOptions({ name: 'PickemRewardsSection' })

const props = defineProps<{
  tournament: Tournament
}>()

const { t } = useI18n()
const { $snacks } = useNuxtApp()

const accountStore = useAccountStore()
const { isAuthorized } = storeToRefs(accountStore)
const rewardsStore = useRewardsStore()
const { trackRewardClick } = usePickemAnalytics()
const { levels, userXp, isLoading, hasClaimable } = storeToRefs(rewardsStore)
const { error } = await useAsyncData(
  `pickem-rewards-${props.tournament.id}`,
  () => rewardsStore.fetchRewards(props.tournament.id),
  { watch: [() => props.tournament.id, isAuthorized] }
)

watch(
  hasClaimable,
  has => {
    if (has) rewardsStore.startPolling()
    else rewardsStore.stopPolling()
  },
  { immediate: true }
)

onMounted(() => {
  if (hasClaimable.value) rewardsStore.startPolling()
})

onBeforeUnmount(() => {
  rewardsStore.stopPolling()
})

watch(
  error,
  e => {
    if (e) $snacks?.error?.(t('errors.generic'))
  },
  { immediate: true }
)

// Целевой XP — последний уровень (для отображения в шапке «N / max»).
const targetXp = computed(() => {
  let max = 0
  for (const l of levels.value) {
    if (l.points_required > max) max = l.points_required
  }
  return max
})

const { formatted: formattedXp } = usePickemCounter(() => userXp.value)
const { formatted: formattedTarget } = usePickemCounter(() => targetXp.value)

/** Доля заполнения сегмента уровня `index`: [0, P₀] или [Pᵢ₋₁+1, Pᵢ]. */
const segmentFillPercent = (index: number): string => {
  const lv = levels.value
  const level = lv[index]
  if (!level) return '0%'

  const low = index === 0 ? 0 : lv[index - 1]!.points_required + 1
  const high = level.points_required
  const xp = userXp.value

  if (high <= low) return xp >= high ? '100%' : '0%'
  if (xp >= high) return '100%'
  if (xp <= low) return '0%'
  return `${((xp - low) / (high - low)) * 100}%`
}

const isCtaActive = (level: RewardLevel) =>
  level.status === 'available' && level.can_claim

const statusLabel = (level: RewardLevel): string => {
  if (level.status === 'claimed') return t('status.claimed')
  if (level.status === 'available') return t('status.claim')
  return t('status.xp', { n: level.points_required })
}

const rewardImage = (item: RewardItem) =>
  item.resource.image?.medium ?? item.resource.image?.small ?? null

const onClick = async (level: RewardLevel) => {
  if (!isCtaActive(level)) return

  trackRewardClick({
    level,
    tournament: props.tournament
  })

  try {
    await rewardsStore.claim(level.id)
  } catch (err) {
    const status =
      (err as { statusCode?: number; status?: number })?.statusCode ??
      (err as { status?: number })?.status
    const code = (err as { data?: { code?: string } })?.data?.code

    let message = t('errors.generic')
    if (status === 423 || code === 'auto_claim_imminent') {
      message = t('errors.locked')
      rewardsStore.fetchRewards(props.tournament.id).catch(() => undefined)
    } else if (status === 422) {
      message = t('errors.notEnoughXp')
    } else if (code === 'tournament_finished') {
      message = t('errors.finished')
    }
    $snacks?.error?.(message)
  }
}
</script>

<i18n lang="yaml">
ru:
  aria: Награды и прогресс
  title: Награды и прогресс
  empty: У этого турнира пока нет наград
  status:
    claimed: Получено
    claim: Забрать
    xp: '{n}xp'
  blocked: Автозачисление
  errors:
    locked: Награда временно недоступна — скоро будет выдана автоматически
    finished: Турнир завершён, призы больше нельзя забрать вручную
    notEnoughXp: Недостаточно XP для этой награды
    generic: Не удалось забрать награду. Попробуйте позже
</i18n>

<style lang="scss" module>
@use './variables' as vars;
.section {
  display: flex;
  flex-direction: column;
  gap: 1.6em;
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2em;
}

.title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.6em;
  @include typo-get(subhead-m);
  color: color-get(--root-rgb-text-1);

  @include down(sm) {
    @include typo-get(subhead-s);
  }
}

.titleIcon {
  width: 1.4em;
  height: 1.4em;
  color: color-get(--root-rgb-text-2);
}

.xp {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  color: color-get(--root-rgb-text-1);
  font-variant-numeric: tabular-nums;
}

.xpIcon {
  width: 1.6em;
  height: 1.6em;
  color: color-get(--root-rgb-text-2);

  @include down(sm) {
    width: 1.4em;
    height: 1.4em;
  }
}

.xpCurrent {
  @include typo-get(subhead-m);

  @include down(sm) {
    @include typo-get(subhead-s);
  }
}

.xpTotal {
  @include typo-get(body-m);
  color: color-get(--root-rgb-text-3);
  font-weight: 400;

  @include down(sm) {
    @include typo-get(body-s);
  }
}

.body {
  display: flex;
  flex-direction: column;
  gap: 0.6em;

  @include down(sm) {
    margin: 0 calc(var(--pickem-details-padding) * -1);
  }
}

.rewardsRow {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.75em;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  @include custom-scrollbar();

  &::-webkit-scrollbar {
    height: 0.6em;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--root-rgb-bg-card), 1);
  }

  @include down(md) {
    @include hide-scrollbar;
  }

  @include down(sm) {
    padding: 0 var(--pickem-details-padding);
  }
}

.rewardCol {
  box-sizing: border-box;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25em;
}

.segmentTrack {
  position: relative;
  width: 100%;
  height: 0.375em;
  flex-shrink: 0;
  border-radius: 1.25em;
  overflow: hidden;
}

.segmentTrackBg {
  position: absolute;
  inset: 0;
  background-color: color-get(--root-rgb-bg-card);
  border-radius: inherit;
}

.segmentFill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  max-width: 100%;
  border-radius: inherit;
  background: linear-gradient(
    180deg,
    color-get(--root-rgb-accent-2) 0%,
    color-get(--root-rgb-accent-3) 100%
  );
  transition: width 0.3s ease;
}

.cellInner {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.8em;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
  padding: 0.4em;
  cursor: default;
  color: inherit;
  text-align: center;

  &:disabled {
    cursor: default;
  }
}

.cellTop {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3em;
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-3);
}

.cellTopLocked {
  color: color-get(--root-rgb-text-2);
}

.cellXpIcon {
  width: 0.875em;
  height: 0.875em;
  flex-shrink: 0;
  color: color-get(--root-rgb-text-2);
}

.rewardCol-available {
  .cellInner {
    cursor: pointer;
  }

  .cellImage {
    border: 1px solid color-get(--root-rgb-accent-2);
    background: var(--root-gradient-system-success-bg);
  }

  .cellStatus {
    color: color-get(--root-rgb-accent-3);
  }

  .cellName {
    color: color-get(--root-rgb-accent-2);
  }
}

.cellMedia {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.cellMediaItem {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  width: 100%;
  max-width: 10em;
  min-width: 7em;

  @include down(md) {
    min-width: 10em;
  }
}

.cellImageCheck {
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  z-index: 2;
  width: 2em;
  height: 2em;
  color: color-get(--root-rgb-accent-3);
}

.cellMediaPlus {
  position: absolute;
  left: 50%;
  top: 2.3em;
  transform: translateX(-50%);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4em;
  height: 1.4em;
  padding: 0.3em;
  border-radius: 50%;
  color: color-get(--root-rgb-text-2);
  background-color: color-get(--root-rgb-control-default-bg);
  z-index: 1;
}

.cellImage {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 6em;
  border-radius: 1.2em;
  background-color: color-get(--root-rgb-bg-card);
  overflow: hidden;
  flex-shrink: 0;
  min-width: 0;

  img {
    width: 6em;
    object-fit: contain;
  }
}

.cellMediaDouble .cellMediaItem {
  width: 50%;
  min-width: 7.5em;
}

.rewardCol-claimed .cellImage {
  background-color: color-get(--root-rgb-bg-tone-dark);

  img {
    opacity: 0.5;
  }
}

.rewardCol-claimed .cellName {
  color: color-get(--root-rgb-text-2);
  opacity: 0.5;
}

.cellImageFallback {
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-3);
}

.cellName {
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-2);
  // Ограничим в 2 строки, как в макете
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.cellHint {
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-3);
}

.empty {
  margin: 0;
  padding: 1.2em 0;
  text-align: center;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-2);
}
</style>
